// eslint-disable-next-line
import React, { useState, useEffect,MouseEvent } from 'react';
import './rules.less';
import {
  Layout, Popconfirm, Card, Button, Tabs,
} from 'antd';
import { MinusCircleOutlined, MenuOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Formik, FieldArray, FormikValues, ArrayHelpers, FormikProps,
} from 'formik';
import {
  Form, Table, Input,
} from 'formik-antd';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import RulesPseudoCode from './rules-pseudo-code/rules-pseudo-code';
import { useStore } from '../../../store';
import PageTitle from '../../lib/page-title';

import AddButton from './add.button';

import SecondaryButton from '../../lib/secondary-button';
import PrimaryButton from '../../lib/primary-button';
import { USER_ID } from '../../../store/modules/user/user.constants';
import { useActions } from '../../providers/actions-provider/actions.context';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';
import { IMPORTS_KEY } from '../../../store/modules/tenants/tenant.constants';
import RulesWrapper from './final-rules/final-rules';
import checkPermission from '../../../utils/permissions/permissions';
import PermissionProvider from '../../providers/permissionProvider';

interface IFieldArrayRefProps {
  arrayHelpers: ArrayHelpers;
}

class FieldArrayRef extends React.Component<IFieldArrayRefProps> {
  getArrayHelpers() {
    const { arrayHelpers } = this.props;
    return arrayHelpers;
  }

  render() {
    return null;
  }
}

const Rules = (): JSX.Element => {
  const {
    currentUser,
    tenantRules,
    tenantRulesSchema,
    tenantRulesSaveState,
    rulesData,
    transformsData,
    systemConfigData,
    scheduledRulesData,
  } = useStore();
  const [rulesReload, setRulesReload] = useState(true);
  const [pseudocodeReload, setPseudocodeReload] = useState(false);
  const { userActions, tenantActions } = useActions();
  const localStorageService = useLocalStorage();
  const userID: number | null = localStorageService.get(USER_ID);

  const hasNotWritePermission = !checkPermission('rulesold', 'write');
  useEffect(() => {
    if (userID) {
      userActions.getCurrentUser(userID);
    }
  }, []);

  useEffect(() => {
    if (currentUser.data.tenant && rulesReload) {
      setRulesReload(false);
      tenantActions.getTenantRulesConfig(currentUser.data.tenant);
      tenantActions.getRulesFields();
    }
  }, [currentUser, rulesReload]);

  const renderAddImportButton = (values: FormikValues) => (
    <AddButton
      values={values}
      label="Add import"
      name={IMPORTS_KEY}
      // eslint-disable-next-line
      rowCreator={(name: string, formValues: any) => {
        const { length = 0 } = formValues.imports;
        return {
          key: `${IMPORTS_KEY}_${length}`,
          name: '',
          data: [],
        };
      }}
    />
  );

  const renderImportButtons = (
    values: FormikValues,
    importIndex: number,
    arrayHelpers: ArrayHelpers,
  ) => (
    <span>
      <Popconfirm
        disabled={hasNotWritePermission}
        title="Are you sure you want to delete this record?"
        onConfirm={() => arrayHelpers.remove(importIndex)}
      >
        <Button
          disabled={hasNotWritePermission}
          type="link"
          block
          onClick={(event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
          }}
          style={{ width: '150px' }}
        >
          Remove
          <MinusCircleOutlined style={{ fontSize: '20px' }} />
        </Button>
      </Popconfirm>
      <AddButton
        values={values}
        label={`Add ${values.imports[importIndex] ? values.imports[importIndex].name : ''}`}
        name={`imports.${importIndex}.data`}
        // eslint-disable-next-line
        rowCreator={(name: string, vals: any) => {
          const parts = name.split('.');
          const imp = vals.imports[parts[1]];
          return {
            key: `${imp.name}_${imp.data.length}`,
            header: '',
            column: '',
            pad: '',
          };
        }}
        style={{ width: '200px' }}
      />
    </span>
  );

  /* eslint-disable */
  const onTransformOrRuleChange = (
    values: FormikValues, 
    arrayHelpers:ArrayHelpers,
    name: string,
    record: any,
    index: number,
  ) => (value: string) => {
    const schema = (tenantRulesSchema.data as any)[name];
    const data = values[name];
    const schemaForType = schema[value] || {};
    const replacement = { ...record, properties: {} };
    delete replacement.placeholder;
    replacement.name = value;

    if (schemaForType) {
      if (schemaForType.properties?.length > 0) {
        schemaForType.properties.forEach((p: any) => {
          replacement.properties[p.name] = '';
        });  
      }
      replacement.sort = schemaForType.sort || 0;
    }
    
    let newIndex = data.findIndex((d: any) => d.sort === replacement.sort);

    if (newIndex > -1) {
      for (let i = newIndex; i < data.length; i++) {
        const e = data[i];
        if (e.sort === replacement.sort) {
          newIndex = i;
        }
      }
  
      if (newIndex === index) {
        arrayHelpers.replace(index, replacement);
      } else {
        arrayHelpers.remove(index);
        arrayHelpers.insert(newIndex + 1, replacement);
      }
    } else {
      for (let i = 0; i < data.length; i++) {
        const e = data[i];
        if (replacement.sort > e.sort ) {
          newIndex = i;
        } else {
          break;
        }
      }
      arrayHelpers.remove(index);
      arrayHelpers.insert(newIndex + 1, replacement);
    }
  };  

  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragStart(event: any) {
    const { active } = event;
    console.log('active', active);
    console.log('event', event);
    setActiveId(active.id);
  }

  function handleDragEnd (name: string, values: FormikValues, arrayHelpers: ArrayHelpers) {
    return (event: any) => {
      const { active, over } = event;
      if (active.id !== over.id) {
        // in case is a nested array
        if (name.indexOf('.') > -1) {
          // we have 2 levels for sure, so at most we have
          const nodeNames = name.split('.');
          const dataName = nodeNames[0];
          const index = +nodeNames[1];
          const data = values[dataName][index].data;
          const oldIndex = data.findIndex((item: any) => item.key === active.id);
          const newIndex = data.findIndex((item: any) => item.key === over.id);
          const oldRow = data[oldIndex];
          const newRow = data[newIndex];
          if (oldIndex !== newIndex && oldRow && newRow) {
            arrayHelpers.swap(oldIndex, newIndex);
          }
        } else {
          const data = values[name];
          const oldIndex = data.findIndex((item: any) => item.key === active.id);
          const newIndex = data.findIndex((item: any) => item.key === over.id);          
          const oldData = data[oldIndex];
          const newData = data[newIndex];
          if (oldIndex !== newIndex 
            && oldData 
            && newData 
            && oldData.sort === newData.sort) {
            arrayHelpers.swap(oldIndex, newIndex);
          }
        }
      }
      setActiveId(null);  
    }
  }  

  /* eslint-disable */
  function DraggableWrapper(props: any) {
    const { children, ...restProps } = props;
    /**
     * 'children[1]` is `dataSource`
     * Check if `children[1]` is an array
     * because antd gives 'No Data' element when `dataSource` is an empty array
     */
    return (
      <SortableContext
        items={children[1] instanceof Array ? children[1].map((child: any) => child.key) : []}
        strategy={verticalListSortingStrategy}
        {...restProps}
      >
        <tbody {...restProps}>
          {
            // This invokes `Table.components.body.row` for each element of `children`.
            children
          }
        </tbody>
      </SortableContext>
    );
  }

  function DraggableRow(props: any) {
    const { attributes, listeners, setNodeRef, isDragging, overIndex, index } = useSortable({
      id: props["data-row-key"],
    });
    const isOver = overIndex === index
    const { children, ...restProps } = props;
    const isData = children instanceof Array;
    const style = {
      ...restProps?.style,
      ...(isData && isDragging ? { background: "#80808038" } : {}),
      ...(isData && isOver ? { borderTop: "5px solid #ec161638" } : {})
    }

    return (
      <tr
        ref={setNodeRef}
        {...attributes}
        {...restProps}
        style={style}
      >
        {
          React.Children.map(
            children, (child: any) => {
              const { key, ...restProps } = child;
              delete restProps['$$typeof'];
              if (key === 'dragHandle') {
                // console.log('adding drag listeners', child.props);
                // return React.cloneElement(child, { ...listeners, ...restProps });
                // return <child.type key={key} {...listeners} {...restProps} />
                return (
                  <td {...listeners} {...restProps}>
                    {child.props.render('', child.props.record)}
                  </td>
                );
              } 
              
              return child;
            }
          )
        }
      </tr>
    );
  } 
  /* eslint-enable */

  const buildImportColumns = (name: string, arrayHelpers: ArrayHelpers) => [{
    key: 'dragHandle',
    // dataIndex: 'sort',
    width: '1%',
    // className: 'drag-visible',
    // eslint-disable-next-line
    render: (text: string, record: any) => (
      hasNotWritePermission ? '' : <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
    ),
  },
  {
    title: 'AVP-Platform Property',
    key: 'header',
    width: '50%',
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => (
      <Input
        disabled={hasNotWritePermission}
        className="field__row-input"
        name={`${name}.${i}.header`}
      />
    ),
  }, {
    title: 'Import File Column Index',
    key: 'column',
    width: '40%',
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => (
      <Input
        disabled={hasNotWritePermission}
        className="field__row-input"
        name={`${name}.${i}.column`}
      />
    ),
  }, {
    title: '',
    key: 'delete',
    dataIndex: 'delete',
    // width: 50,
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => (
      <Popconfirm
        disabled={hasNotWritePermission}
        title="Are you sure you want to delete this record?"
        onConfirm={() => arrayHelpers.remove(i)}
      >
        <SecondaryButton
          disabled={hasNotWritePermission}
          type="button"
          className="button-execute ant-btn--small"
        >
          delete
        </SecondaryButton>
      </Popconfirm>
    ),
  }];

  const {
    transforms, rules, imports = [],
  } = tenantRules.data;
  const importsTab = (
    <Formik
      initialValues={{
        transforms,
        rules,
        imports,
      }}
      onSubmit={async (values: FormikValues) => {
        // eslint-disable-next-line
        const processedImports: any = {};
        // eslint-disable-next-line
        values.imports.forEach((element: any) => {
        // eslint-disable-next-line
        const processedTable = element.data.reduce((accumulator: any, element: any) => {
            accumulator.headers.push(element.header);
            accumulator.columns.push(element.column);
            accumulator.pads.push(element.pad);
            return {
              ...accumulator,
            };
          }, { headers: [], columns: [], pads: [] });

          processedImports[element.name] = processedTable;
        });

        const payload = {
          ...values,
          imports: processedImports,
        };

        await tenantActions.saveTenantRulesConfig(currentUser.data.tenant, payload);
        await tenantActions.getTenantRulesConfig(currentUser.data.tenant);
      }}
      enableReinitialize
    >
      {function rulesForm({ values, dirty, isSubmitting }: FormikProps<FormikValues>) {
        return (
          <Form>
            <Card style={{ borderBottom: 0 }} extra={renderAddImportButton(values)}>
              <FieldArray name={IMPORTS_KEY}>
                {(importsArrayHelpers: ArrayHelpers) => (
                  <>
                    <FieldArrayRef arrayHelpers={importsArrayHelpers} />
                    {/* eslint-disable-next-line */}
                    {values.imports && values.imports.map((importData: any, keyIndex: number) => (
                      <Card
                        // eslint-disable-next-line
                        key={`import_card_${keyIndex}`}
                        title={(
                          <div>
                            <span className="title-field-container">Map Name:</span>
                            <Input
                              disabled={hasNotWritePermission}
                              className="field__row-input"
                              name={`imports.${keyIndex}.name`}
                              style={{ width: '300px' }}
                            />
                          </div>
                        )}
                        extra={renderImportButtons(values, keyIndex, importsArrayHelpers)}
                      >
                        <FieldArray name={`imports.${keyIndex}.data`}>
                          {(arrayHelpers: ArrayHelpers) => (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragStart={handleDragStart}
                              onDragEnd={handleDragEnd(`imports.${keyIndex}.data`, values, arrayHelpers)}
                            >
                              <Table
                                className="table"
                                name={`imports.${keyIndex}.data`}
                                columns={buildImportColumns(`imports.${keyIndex}.data`, arrayHelpers)}
                                bordered={false}
                                loading={tenantRules.loading}
                                pagination={false}
                                components={{
                                  body: {
                                    wrapper: DraggableWrapper,
                                    row: DraggableRow,
                                  },
                                }}
                              />
                              <DragOverlay>
                                {activeId && (<div style={{ width: '100%', borderBottom: '1px solid red' }} />)}
                              </DragOverlay>
                            </DndContext>
                          )}
                        </FieldArray>
                      </Card>
                    ))}
                  </>
                )}
              </FieldArray>
            </Card>
            <PrimaryButton
              type="submit"
              className="fields-table__button-next"
              style={{ width: 280 }}
              disabled={!dirty || hasNotWritePermission}
              loading={tenantRulesSaveState.loading || isSubmitting}
            >
              Save changes
            </PrimaryButton>
          </Form>
        );
      }}
    </Formik>
  );

  const thirdTab = (
    <RulesPseudoCode reload={pseudocodeReload} />
  );

  const finalRulesTab = (
    <RulesWrapper
      target="rules"
      rules={rulesData.data.rules}
      loading={rulesData.loading && !rulesData.loaded}
    />
  );
  const finalTransformsTab = (
    <RulesWrapper
      target="transforms"
      rules={transformsData.data.rules}
      loading={transformsData.loading && !transformsData.loaded}
    />
  );
  const systemConfigRules = (
    <RulesWrapper
      target="systemConfig"
      rules={systemConfigData.data.rules}
      loading={systemConfigData.loading && !systemConfigData.loaded}
    />
  );
  const scheduledRules = (
    <RulesWrapper
      target="scheduledRules"
      rules={scheduledRulesData.data.rules}
      loading={scheduledRulesData.loading && !scheduledRulesData.loaded}
    />
  );

  const labelWithInfo = (label: string, url: string | null = null) => (
    <span>
      {label}
      {
        url && (
          <InfoCircleOutlined
            style={{ padding: '5px', fontSize: '20px' }}
            onClick={() => userActions.getHelp(url)}
          />
        )
      }
    </span>
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: any = [];
  const hasNewRulesAccess = checkPermission('rules', 'read');
  const hasOldRulesAccess = checkPermission('rulesold', 'read');

  items.push({
    label: labelWithInfo('System Config', '/docs/User-Manual/rules/#system-config'),
    key: '0',
    children: systemConfigRules,
  });

  if (hasOldRulesAccess) {
    items.push({
      label: labelWithInfo('Imports', '/docs/User-Manual/rules/#imports'),
      key: '1',
      children: importsTab,
    });
  }

  items.push({
    label: labelWithInfo('Transforms', '/docs/User-Manual/rules/#transforms'),
    key: '2',
    children: finalTransformsTab,
  });
  if (hasNewRulesAccess) {
    items.push(
      {
        label: labelWithInfo('Rules', '/docs/User-Manual/rules/#rules_1'),
        key: '3',
        children: finalRulesTab,
      },
      {
        label: labelWithInfo('Pseudocode', '/docs/User-Manual/rules/#psuedo-code'),
        key: '4',
        children:
        thirdTab,
      },
      { label: labelWithInfo('Scheduled Rules', '/docs/User-Manual/rules/#scheduled-rules'), key: '5', children: scheduledRules },
    );
  }

  return (
    <Layout className="main-container">
      <PermissionProvider pageId="rulesold,rules">
        <PageTitle title="Rules">
          {/* <PrimaryButton title="New User" onClick={goToCreation} /> */}
        </PageTitle>
        <Tabs
          // defaultActiveKey="3"
          style={{ width: '100%' }}
          items={items}
          onTabClick={(key: string) => {
            if (key === '4') {
              setPseudocodeReload(!pseudocodeReload);
            }
          }}
          // destroyInactiveTabPane
        />
      </PermissionProvider>
    </Layout>
  );
};

Rules.displayName = 'Rules';
export default Rules;
